import type { FunctionComponent } from "react"
import { useState } from "react"
import { connect } from "react-redux"
import Select from "react-select"
import approvalService from "src/components/approvals/ApprovalService"
import type { AppState } from "src/store"
import type ReferenceTypes from "src/store/UserReference/types"
import vehicleIncidentColorSet from "../../constants/VehicleIncidentColorSet"
import { LoadState } from "../../enums"
import VIS from "../../VehicleIncidentsService"

// Styles
import "./VehicleIncidentsFilters.css"

interface Props {
	reference: ReferenceTypes
}

interface Option {
	label: string
	value: string | number
	code?: string
}

const formatLabelKey = (key: string): string =>
	key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase())

const labelOptions: Option[] = Object.keys(vehicleIncidentColorSet).map(
	(key) => ({
		label: formatLabelKey(key),
		value: key,
	}),
)

const VehicleIncidentsFilters: FunctionComponent<Props> = ({ reference }) => {
	const [selectedState, setSelectedState] = useState<Option | null>(null)
	const [selectedEmployee, setSelectedEmployee] = useState<Option | null>(null)
	const [selectedLabels, setSelectedLabels] = useState<Option[]>([])

	if (!reference) return null

	const stateOptions: Option[] = reference.allStates.map((s) => ({
		label: s.StateName,
		value: s.StateID,
		code: s.StateCode,
	}))

	const employeeOptions: Option[] =
		reference.supervisors
			?.map((supervisor): Option | null => {
				const emp = approvalService.retrieveEmployeeFromMap(supervisor.id)
				return emp
					? {
							label: `${emp.FirstName} ${emp.LastName}`,
							value: emp.EmployeeID,
						}
					: null
			})
			.filter((opt): opt is Option => opt !== null) /* <-- type‑narrowing */ ??
		[]

	const applyFiltersAndRefresh = () => {
		VIS.setLoader(LoadState.ApplyingFilters)
		VIS.refresh(VIS.currentView)
	}

	const handleStateChange = (opt: Option | null) => {
		setSelectedState(opt)
		VIS.filters.stateCode = opt?.code ?? undefined
		applyFiltersAndRefresh()
	}

	const handleEmployeeChange = (opt: Option | null) => {
		setSelectedEmployee(opt)
		VIS.filters.employeeId = opt ? Number(opt.value) : undefined
		applyFiltersAndRefresh()
	}

	const handleLabelChange = (opts: readonly Option[] | null) => {
		const selected = Array.from(opts ?? [])
		setSelectedLabels(selected)
		VIS.filters.labels =
			selected.length > 0 ? selected.map((o) => String(o.value)) : undefined
		applyFiltersAndRefresh()
	}

	return (
		<div className="VehicleIncidentsFilters">
			{/* State */}
			<div className="VehicleIncidentsFiltersItem">
				<Select
					placeholder="State"
					value={selectedState}
					onChange={handleStateChange}
					options={stateOptions}
					isClearable
				/>
			</div>
			{/* Employee */}
			<div className="VehicleIncidentsFiltersItem">
				<Select
					placeholder="Employee"
					value={selectedEmployee}
					onChange={handleEmployeeChange}
					options={employeeOptions}
					isClearable
				/>
			</div>
			{/* Label */}
			<div className="VehicleIncidentsFiltersItem">
				<Select
					placeholder="Label"
					value={selectedLabels}
					onChange={handleLabelChange}
					options={labelOptions}
					isMulti
					isClearable
				/>
			</div>
		</div>
	)
}

const mapStateToProps = (state: AppState) => ({
	reference: state.userReference.userReference,
})

export default connect(mapStateToProps)(VehicleIncidentsFilters)
